import {
  CalculationResult,
  Inputs,
  INPUT_MONTHS,
  calculateModel,
  day15Share,
  day45Share,
  getLeaderMonths,
  getPricing,
  getRampShares,
} from './roiCalculations'
import { profitByMonth } from './commissions'

export type RepHorizonKey = '90d' | '6mo' | '1y'

export interface RepHorizon {
  key: RepHorizonKey
  label: string
  // Expected realized sales from one rep who made it to Day 90, counted from
  // that rep's launch.
  sales: number
  grossProfit: number
  net: number
  // Net ROI on the cost to produce the rep: (gross profit − cost) ÷ cost.
  // Shown as a gross multiple (gross profit ÷ cost) via roiMultiple.
  multiple: number
}

export interface RepEconomics {
  costPer90DayRep: number
  hiresPer90DayRep: number
  /** One steady campaign month (the cohort the math is built on). */
  cohortHires: number
  cohortLaunched: number
  /** Day-90 reps from that month's hires (unrounded; shown to 2 decimals). */
  cohortNinetyDayReps: number
  cohortFees: number
  cohortAdSpend: number
  /** Estimated bonuses that month's hires earn. */
  cohortBonuses: number
  cohortCost: number
  horizons: RepHorizon[]
}

export interface RepScenario {
  halfSales?: boolean
  doubleAttrition?: boolean
}

const HORIZONS: { key: RepHorizonKey; label: string; months: number }[] = [
  { key: '90d', label: 'First 90 days', months: 3 },
  { key: '6mo', label: '6 months', months: 6 },
  { key: '1y', label: '1 year', months: 12 },
]

export const round1 = (value: number) => Math.round(value * 10) / 10

// Unit economics for a single rep who reaches Day 90, built from one steady
// month of the standard campaign so it can be checked on a napkin:
//
//   cost of the month (D2D HIRE fees + ad spend + the estimated bonuses that
//   month's hires earn) ÷ that month's Day-90 reps (hires × launch rate ×
//   Day-30 rate × Day-30-to-90 rate) = cost per Day-90 rep.
//
// Every hire who washes out is paid for by the reps who stay. The leader
// search is never included, so Find a leader doesn't move these figures.
//
// Gross profit: that rep's sales month by month from their launch — the ramp
// curve for months 1-3, then the veteran rate tapering with one-year
// retention — run through the same pullthrough, ticket, residual, and margin
// rules as the projection.
//
// Scenarios: 50% Sales halves the rep's output. 2x Attrition halves the reps
// who reach each stage, so bonuses fall with them and each Day-90 rep costs
// twice as much. It does not also speed up the fall-off after Day 90: the
// projection models 2x Attrition as half the reps, and penalising the
// survivors a second time would make the 6-month and 1-year figures disagree
// with the projection.
export function calculateRepEconomics(
  inputs: Inputs,
  result: CalculationResult,
  scenario: RepScenario = {},
): RepEconomics | null {
  const campaignResult =
    getLeaderMonths(inputs) > 0
      ? calculateModel({
          ...inputs,
          leaderCampaignMonths: 0,
          leaderMonth2Sales: undefined,
        })
      : result
  // Campaign Month 12: the steady run rate after any ad ramp, with no
  // first-month prepayment in it.
  const row = campaignResult.rows[INPUT_MONTHS]
  if (!row) return null

  const survival = scenario.doubleAttrition ? 0.5 : 1
  const pct30 = clampPct(inputs.pct30)
  const pct90 = clampPct(inputs.pct90)
  const pct60 = Math.sqrt(pct90)
  const launched = row.launched * survival
  const cohortNinetyDayReps = row.launched * pct30 * pct90 * survival

  const pricing = getPricing(inputs)
  const bonusesOn = !!inputs.pkg && inputs.salesSchoolEnabled !== false
  const cohortBonuses = bonusesOn
    ? Math.ceil(launched * day15Share(pct30)) * pricing.bonusDay15 +
      Math.ceil(launched * day45Share(pct30, pct60)) * pricing.bonusDay45 +
      Math.round(cohortNinetyDayReps) * pricing.bonusDay90
    : 0
  const cohortFees = row.hireCost
  const cohortAdSpend = row.adSpend
  const cohortCost = cohortFees + cohortAdSpend + cohortBonuses
  if (cohortNinetyDayReps <= 0 || cohortCost <= 0) return null

  const costPer90DayRep = cohortCost / cohortNinetyDayReps
  const hiresPer90DayRep = row.hires / cohortNinetyDayReps

  const monthlySales = repMonthlySales(inputs, scenario, 12)
  const monthlyGrossProfit = grossProfitByMonth(inputs, monthlySales)

  const horizons = HORIZONS.map(({ key, label, months }) => {
    const sales = sum(monthlySales.slice(0, months))
    const grossProfit = sum(monthlyGrossProfit.slice(0, months))
    return {
      key,
      label,
      sales,
      grossProfit,
      net: grossProfit - costPer90DayRep,
      // ROI = (gain − cost) ÷ cost
      multiple: (grossProfit - costPer90DayRep) / costPer90DayRep,
    }
  })

  return {
    costPer90DayRep,
    hiresPer90DayRep,
    cohortHires: row.hires,
    cohortLaunched: row.launched,
    cohortNinetyDayReps,
    cohortFees,
    cohortAdSpend,
    cohortBonuses,
    cohortCost,
    horizons,
  }
}

// Realized (post-pullthrough) sales for one 90-day rep, month by month.
export function repMonthlySales(
  inputs: Inputs,
  scenario: RepScenario,
  months: number,
): number[] {
  const vet = Math.max(0, inputs.salesVetMonthly)
  const ramp = getRampShares(inputs)
  const salesFactor =
    clampPct(inputs.pullthrough) * (scenario.halfSales ? 0.5 : 1)
  // Veterans taper monthly so one-year retention holds after Day 90 — the
  // same rate the projection uses for every cohort.
  const monthlyRetention = Math.pow(clampPct(inputs.pct1y), 1 / 9)
  return Array.from({ length: months }, (_, m) => {
    const raw = m < 3 ? vet * ramp[m] : vet * Math.pow(monthlyRetention, m - 3)
    return raw * salesFactor
  })
}

// Mirrors the projection's revenue rules: the initial ticket in the month of
// sale, plus monthly or quarterly residuals with customer attrition.
function grossProfitByMonth(inputs: Inputs, sales: number[]): number[] {
  return repCashByMonth(inputs, sales).profit
}

/** Revenue and gross profit (or overrides) from a rep's sales, by month. */
export function repCashByMonth(
  inputs: Inputs,
  sales: number[],
): { revenue: number[]; profit: number[] } {
  const ticket = Math.max(0, inputs.ticket)
  const gpm = clampPct(inputs.gpm)
  const residualTicket = Math.max(0, inputs.residualTicket || 0)

  const residual = sales.map((_, m) => {
    let revenue = 0
    if (inputs.revenueCadence === 'monthly' && residualTicket) {
      const retention = 1 - clampPct(inputs.monthlyAttrition || 2)
      for (let source = 0; source < m; source++) {
        revenue +=
          sales[source] * Math.pow(retention, m - source - 1) * residualTicket
      }
    } else if (inputs.revenueCadence === 'quarterly' && residualTicket) {
      const retention = 1 - clampPct(inputs.quarterlyAttrition || 5)
      if (m >= 3) revenue += sales[m - 3] * retention * residualTicket
    }
    return revenue
  })
  // Same commission cash flow as the projection (none unless modeled).
  const revenue = sales.map((n, m) => n * ticket + residual[m])
  return {
    revenue,
    profit: profitByMonth(inputs, sales, residual, revenue, gpm).profit,
  }
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0)
}

function clampPct(value: number): number {
  return Math.min(1, Math.max(0, (value || 0) / 100))
}
